package puzzlekey

/**
 * A camera frame in the layout of the browser's ImageData: 4 bytes (R, G, B, A) per pixel, row by row.
 * On the site, `ImageData.data` (a Uint8ClampedArray) is viewed as an Int8Array over the same buffer.
 */
class Rgba(val width: Int, val height: Int, val bytes: ByteArray) {
    init { require(bytes.size == width * height * 4) { "expected ${width * height * 4} bytes, got ${bytes.size}" } }
}

/** A pixel rectangle inside a frame; for a Scan, the guide overlay. */
data class Rect(val x: Int, val y: Int, val w: Int, val h: Int)

/** A square grayscale image, row by row, values 0..1. */
class Gray(val size: Int, val pixels: FloatArray)

/** Side of the square image [normalise] produces, in pixels. */
const val NORMALISED_SIZE = 64

/**
 * The Puzzle picture inside [guide], as the hashes see it (ADR-0004):
 * cropped to the guide, grayscale, squeezed to 64×64 whatever the guide's aspect, with
 * brightness and contrast stretched so daylight, a warm lamp and a dim room look alike.
 * The stretch runs after the resize, so a glare spot is already averaged into its surroundings.
 */
fun normalise(frame: Rgba, guide: Rect): Gray {
    require(guide.x >= 0 && guide.y >= 0 && guide.x + guide.w <= frame.width && guide.y + guide.h <= frame.height) {
        "guide $guide is outside the ${frame.width}×${frame.height} frame"
    }
    val small = resize(luma(frame, guide), guide.w, guide.h, NORMALISED_SIZE)
    return Gray(NORMALISED_SIZE, stretch(small))
}

/** Share of the darkest and of the brightest pixels clipped by [stretch]. */
private const val CLIP = 0.02f

/**
 * Maps the [CLIP] and 1 − [CLIP] percentiles to 0 and 1 and clamps the rest, so a glare
 * spot or a deep shadow can't squash the picture's contrast.
 */
private fun stretch(pixels: FloatArray): FloatArray {
    val sorted = pixels.sortedArray()
    val lo = sorted[(CLIP * (sorted.size - 1)).toInt()]
    val hi = sorted[((1 - CLIP) * (sorted.size - 1)).toInt()]
    if (hi - lo < 1e-3f) return FloatArray(pixels.size) { 0.5f } // a blank wall: no contrast to stretch
    return FloatArray(pixels.size) { ((pixels[it] - lo) / (hi - lo)).coerceIn(0f, 1f) }
}

/** Rec. 601 luma of the pixels inside [area], 0..255. */
private fun luma(frame: Rgba, area: Rect): FloatArray {
    val out = FloatArray(area.w * area.h)
    for (y in 0 until area.h) for (x in 0 until area.w) {
        val i = ((area.y + y) * frame.width + area.x + x) * 4
        val r = frame.bytes[i].toInt() and 0xFF
        val g = frame.bytes[i + 1].toInt() and 0xFF
        val b = frame.bytes[i + 2].toInt() and 0xFF
        out[y * area.w + x] = 0.299f * r + 0.587f * g + 0.114f * b
    }
    return out
}

/** Shrinks a [w]×[h] image to [size]×[size]; each output pixel is the mean of the source pixels it covers. */
private fun resize(src: FloatArray, w: Int, h: Int, size: Int): FloatArray {
    val out = FloatArray(size * size)
    for (oy in 0 until size) {
        val y0 = oy * h / size
        val y1 = maxOf(y0 + 1, (oy + 1) * h / size)
        for (ox in 0 until size) {
            val x0 = ox * w / size
            val x1 = maxOf(x0 + 1, (ox + 1) * w / size)
            var sum = 0f
            for (y in y0 until y1) for (x in x0 until x1) sum += src[y * w + x]
            out[oy * size + ox] = sum / ((y1 - y0) * (x1 - x0))
        }
    }
    return out
}
